import React from "react";
import ProductCard from "./ProductCard";

const ProductCardWrapper = ({
  displayedProducts,
  productsLoading,
  onRetry,
  productsError,
}) => {
  // Dynamic Loading Spinner Component
  const LoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center h-[400px] lg:h-[500px]">
      {/* Main Spinner Container */}
      <div className="relative">
        {/* Outer Ring */}
        <div className="w-16 h-16 border-4 border-gray-200 border-t-[#016FFC] rounded-full animate-spin"></div>

        {/* Inner Ring - Counter Rotation */}
        <div className="absolute top-2 left-2 w-12 h-12 border-4 border-transparent border-b-[#6781CF] rounded-full animate-spin animation-reverse"></div>

        {/* Center Dot */}
        <div className="absolute top-6 left-6 w-4 h-4 bg-gradient-to-r from-[#016FFC] to-[#6781CF] rounded-full animate-pulse"></div>
      </div>

      {/* Loading Text */}
      <div className="mt-6 text-center">
        <h3 className="text-lg font-semibold text-[#121212] mb-2">
          Loading Products
        </h3>
        <p className="text-sm text-[#4A4C56] animate-pulse">
          Please wait while we fetch the latest products...
        </p>
      </div>

      {/* Progress Dots */}
      <div className="flex space-x-1 mt-4">
        <div className="w-2 h-2 bg-[#016FFC] rounded-full animate-bounce"></div>
        <div
          className="w-2 h-2 bg-[#016FFC] rounded-full animate-bounce"
          style={{ animationDelay: "0.1s" }}
        ></div>
        <div
          className="w-2 h-2 bg-[#016FFC] rounded-full animate-bounce"
          style={{ animationDelay: "0.2s" }}
        ></div>
      </div>

      {/* Custom CSS for reverse animation */}
      <style jsx>{`
        .animation-reverse {
          animation-direction: reverse;
        }
      `}</style>
    </div>
  );

  // Error State with Retry Component
  const ErrorWithRetry = () => (
    <div className="flex flex-col items-center justify-center h-[400px] lg:h-[500px] text-center px-4">
      {/* Error Icon */}
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
        <svg
          className="w-10 h-10 text-red-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
          ></path>
        </svg>
      </div>

      {/* Error Message */}
      <h3 className="text-xl font-semibold text-[#121212] mb-2">
        Oops! Something went wrong
      </h3>
      <p className="text-[#4A4C56] mb-6 max-w-md">
        We couldn't load the products. This might be due to a network issue or
        server error.
      </p>

      {/* Retry Button */}
      <button
        onClick={onRetry}
        className="px-6 py-3 bg-[#016FFC] text-white font-medium rounded-lg hover:bg-[#0056D3] transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#016FFC] focus:ring-opacity-50 shadow-lg"
      >
        <div className="flex items-center space-x-2">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            ></path>
          </svg>
          <span>Try Again</span>
        </div>
      </button>

      {/* Alternative Actions */}
      <div className="mt-4 text-sm text-[#4A4C56]">
        <p>Or try:</p>
        <div className="flex space-x-4 mt-2">
          <button
            onClick={() => window.location.reload()}
            className="text-[#016FFC] hover:underline"
          >
            Refresh Page
          </button>
          <button
            onClick={() => window.history.back()}
            className="text-[#016FFC] hover:underline"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );

  // No Products Found Component
  const NoProductsFound = () => (
    <div className="flex flex-col items-center justify-center h-[300px] lg:h-[400px] text-center px-4">
      {/* Empty State Icon */}
      <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
        <svg
          className="w-12 h-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M9 9l3-3 3 3"
          ></path>
        </svg>
      </div>

      {/* No Products Message */}
      <h3 className="text-xl font-semibold text-[#121212] mb-2">
        No Products Found
      </h3>
      <p className="text-[#4A4C56] mb-6 max-w-md">
        We couldn't find any products matching your criteria. Try adjusting your
        filters or search terms.
      </p>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 border border-[#016FFC] text-[#016FFC] font-medium rounded-lg hover:bg-[#016FFC] hover:text-white transition-all duration-300"
        >
          Refresh Products
        </button>
        <button
          onClick={onRetry}
          className="px-6 py-2 bg-[#016FFC] text-white font-medium rounded-lg hover:bg-[#0056D3] transition-all duration-300"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  // Main Render Logic
  if (productsLoading) {
    return <LoadingSpinner />;
  }

  if (productsError) {
    return <ErrorWithRetry />;
  }

  if (!displayedProducts || displayedProducts.length === 0) {
    return <NoProductsFound />;
  }

  // Success State - Render Products (5 columns)
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-y-[24px] gap-x-[15px] mt-8">
      {displayedProducts.map((item) => {
        return (
          <ProductCard
            key={item?.id}
            title={item.title}
            info={item.info}
            offer={item.offerPrice}
            actual={item.actualPrice}
            img={item.image}
            id={item.id}
            slug={item?.slug}
            readers_icons={item?.readers_icons}
          />
        );
      })}
    </div>
  );
};

export default ProductCardWrapper;
